import { useBoolean, useToast } from '@chakra-ui/react'
import fileDownload from 'js-file-download'
import { createContext, FC, useCallback, useMemo, useState } from 'react'
import { AiOutlineBarChart } from 'react-icons/ai'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'

import { paramsReportToSendMapping } from '@helper/budget/paramsReportSendMapping'
import { validateGroupName } from '@helper/budget/validateGroupName'
import { validateIfShowColumnInTable } from '@helper/budget/validateIfShowColumnInTable'
import { parseReportBudgetData } from '@helper/grid/budget/report'
import budget from '@service/budget'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import {
  IBudgetReportContext,
  IParamsToSend,
  Pagination,
  IUpdateParams
} from '@base/@types/providers/Budget/Report'
import { reportFilter } from '@base/variables/filters/budget/reportFilter'
import { reportRequest } from '@base/variables/filters/budget/reportRequest'
import ReportBudgetHeader from '@base/variables/headers/budget/report'
const ReportBudgetProvider: FC = ({ children }) => {
  const toast = useToast()

  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useBoolean(true)
  const filtersList: ILayoutFilter[] = reportFilter

  const [filename, setFilename] = useState('Orçamento Relatório - Relatório')

  const [data, updateData] = useImmer<any[]>([])
  const [query, setQuery] = useState('')
  const [hiddenColumns, _updateHiddenColumns] = useImmer<string[]>([])

  const [params, updateParams] = useImmer<any>(reportRequest)
  const [pagination, updatePagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })
  const [headers, updateHeaders] = useImmer<ITableHeader[]>(ReportBudgetHeader)
  const [paramsToSend, setParamsToSend] = useState<IParamsToSend>(() => {
    const paramsFormatted = { ...reportRequest }

    return paramsFormatted
  })

  const clearAllFilters = () => {
    const paramsFormatted = { ...reportRequest }

    updateParams({
      ...params,
      empresaIds: false,
      empresaSelectIds: [],
      centroCdLojaIds: false,
      centroCdLojaSelectIds: [],
      categoriasIds: false,
      categoriasSelectIds: [],
      anosIds: false,
      anosSelectIds: [],
      mesesIds: false,
      mesesSelectIds: [],
      historicoIds: false,
      historicoSelectIds: [],
      operacaoIds: false,
      operacaoSelectIds: [],
      visualizationIds: {
        btLeoLiquido: false,
        vendaLiquida: false,
        vendaBruta: false,
        margem: false
      },
      dimensaoIds: {
        total: false,
        du: false
      }
    })

    setParamsToSend(paramsFormatted)
    if (localStorage.getItem('budgetReportParams')) {
      localStorage.setItem(
        'budgetReportParams',
        JSON.stringify({
          ...params,
          empresaIds: false,
          empresaSelectIds: [],
          centroCdLojaIds: false,
          centroCdLojaSelectIds: [],
          categoriasIds: false,
          categoriasSelectIds: [],
          anosIds: false,
          anosSelectIds: [],
          mesesIds: false,
          mesesSelectIds: [],
          historicoIds: false,
          historicoSelectIds: [],
          operacaoIds: false,
          operacaoSelectIds: [],
          visualizationIds: {
            btLeoLiquido: false,
            vendaLiquida: false,
            vendaBruta: false,
            margem: false
          },
          dimensaoIds: {
            total: false,
            du: false
          }
        })
      )
    }
    if (localStorage.getItem('budgetReportParamsRequest')) {
      localStorage.setItem(
        'budgetReportParamsRequest',
        JSON.stringify(paramsFormatted)
      )
    }
  }

  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    updateParams((draft: { [x: string]: string }) => {
      draft[name] = value
    })

    const paramsToSendFormatted = paramsReportToSendMapping({
      name,
      value,
      compareName: name,
      paramsToSend,
      fieldName: validateGroupName(name)
    })

    setParamsToSend(paramsToSendFormatted)
    if (localStorage.getItem('budgetReportParams')) {
      const newParams: IParamsToSend[] = JSON.parse(
        localStorage.getItem('budgetReportParams') || ''
      )

      localStorage.setItem(
        'budgetReportParams',
        JSON.stringify({ ...newParams, [name]: value })
      )
    }
    if (localStorage.getItem('budgetReportParamsRequest')) {
      localStorage.setItem(
        'budgetReportParamsRequest',
        JSON.stringify(paramsToSendFormatted)
      )
    }
  }
  const handleLocalParams = useCallback(async () => {
    if (localStorage.getItem('budgetReportParams')) {
      const newParams = JSON.parse(
        localStorage.getItem('budgetReportParams') || ''
      )
      updateParams(newParams)
    } else {
      localStorage.setItem('budgetReportParams', JSON.stringify(params))
    }
    if (localStorage.getItem('budgetReportParamsRequest')) {
      const newParams = JSON.parse(
        localStorage.getItem('budgetReportParamsRequest') || ''
      )
      setParamsToSend(newParams)
    } else {
      localStorage.setItem(
        'budgetReportParamsRequest',
        JSON.stringify(paramsToSend)
      )
    }
  }, [updateParams, setParamsToSend, params, paramsToSend])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...headers]

      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show
      }

      updateHeaders(newHeaders)
    },
    [headers, updateHeaders]
  )

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return [
      {
        type: 'export',
        exports: [
          {
            type: 'excel',
            entity: 'budgetReportAnalitc',
            data,
            headers
          },
          {
            type: 'excel',
            entity: 'budgetReportConsolidated',
            data,
            headers
          }
        ]
      },
      {
        type: 'columns',
        data: headers,
        onToggleColumn: toggleColumn
      }
    ]
  }, [data, headers, toggleColumn])

  const handlePageChange = useCallback(
    async (_page: number) => {
      setLoading(true)

      localStorage.setItem(
        '@AlertPurchaseParamsToSend:',
        JSON.stringify(paramsToSend)
      )

      budget
        .getBudgetReport(paramsToSend)
        .then(({ data: { data, pagination } }) => {
          updateData(draft => {
            return draft.filter(item => item.status === 99).concat(data)
          })
          updatePagination(pagination)
          setLoading(false)
        })
        .catch(() => updateData([]))
      setLoading(false)
    },
    [paramsToSend, updateData, updatePagination]
  )
  const updateShowTableInTable = useCallback(
    async (data: any[]) => {
      updateData(draft => {
        return draft.filter(item => item.status === 99).concat(data)
      })

      const headersFormatted = validateIfShowColumnInTable(
        headers,
        paramsToSend
      )

      updateHeaders(headersFormatted)
    },
    [updateData, updateHeaders, headers, paramsToSend]
  )
  const getData = useCallback(async () => {
    const isDisabledProgress =
      paramsToSend.visualizacao.value !== undefined &&
      paramsToSend.dimensao.value !== undefined
    if (!isDisabledProgress) {
      toast({
        title: 'Atenção',
        description: 'Selecionar Visualização e Dimensão nos filtros',
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      return
    }
    setLoading(true)

    const visu = {}
    const visualizacao: any = Object.assign(visu, paramsToSend.visualizacao)

    const dimen = {}
    const dimensao: any = Object.assign(dimen, paramsToSend.dimensao)

    let paramsNew = paramsToSend
    paramsNew = {
      ...paramsNew,
      visualizacao: {
        btLeoLiquido: visualizacao.value === 'btLeoLiquido',
        vendaLiquida: visualizacao.value === 'vendaLiquida',
        vendaBruta: visualizacao.value === 'vendaBruta',
        margem: visualizacao.value === 'margem'
      },
      dimensao: {
        total: dimensao.value === 'total',
        du: dimensao.value === 'du'
      }
    }

    budget
      .getBudgetReport(paramsNew)
      .then(({ data: { data, pagination } }) => {
        updateShowTableInTable(data)
        updatePagination(pagination)
        setFilters.toggle()
        setLoading(false)
        setFilters.toggle()
      })
      .catch(() => {
        updateData([])
        setLoading(false)
        setFilters.toggle()
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paramsToSend,
    toast,
    updateShowTableInTable,
    updatePagination,
    updateData
  ])

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'checkbox',
        icon: <AiOutlineBarChart />,
        isMulti: false
      }
    ]
  }, [])

  const exportDataConsolidated = useCallback(async () => {
    Swal.fire({
      title: 'Exportar dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })

    const obj = {
      ...paramsToSend,
      visualizacao: {
        btLeoLiquido: true,
        vendaLiquida: false,
        vendaBruta: false,
        margem: false
      },
      dimensao: {
        total: true,
        du: false
      },
      pagination
    }
    try {
      const { data: res } = await budget.postExportConsolidated(obj)
      fileDownload(res, filename + '.csv')
      Swal.close()
    } catch (error: any) {
      const { data } = error.response
      Swal.close()
      const message =
        data.response?.status === 400
          ? data.response?.data?.data[0].message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
    }
  }, [paramsToSend, pagination, filename, toast])

  const exportDataAnalitc = useCallback(async () => {
    Swal.fire({
      title: 'Exportar dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    const obj = {
      ano: paramsToSend.ano.ids,
      mes: paramsToSend.mes.ids,
      empresasIds: paramsToSend.empresa.ids,
      lojasIds: paramsToSend.loja.ids,
      gerenteOperacoesIds: paramsToSend.gerenteOperacoes.ids,
      categoriasCompactasIds: paramsToSend.categoriaCompacta.ids,
      historicoId:
        paramsToSend.historico.ids.length > 0
          ? paramsToSend.historico.ids[0]
          : 0
    }
    try {
      const { data: res } = await budget.postExportAnalitc(obj)
      fileDownload(res, filename + '.csv')
      Swal.close()
    } catch (error: any) {
      const { data } = error.response
      Swal.close()
      const message =
        data.response?.status === 400
          ? data.response?.data?.data[0].message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
    }
  }, [
    paramsToSend.ano.ids,
    paramsToSend.mes.ids,
    paramsToSend.empresa.ids,
    paramsToSend.loja.ids,
    paramsToSend.gerenteOperacoes.ids,
    paramsToSend.categoriaCompacta.ids,
    paramsToSend.historico.ids,
    filename,
    toast
  ])

  return (
    <ReportBudgetContext.Provider
      value={{
        loading,

        filtersList,
        pagination,
        tableInteraction,
        hiddenColumns,
        data: parseReportBudgetData(data),
        actions,
        params,
        handlePageChange,

        headers,
        filename,
        filters,
        query,
        setFilename,
        exportDataConsolidated,
        exportDataAnalitc,
        setQuery,
        toggleColumn,
        handleUpdateParams,
        handleLocalParams,
        getData,
        clearAllFilters,

        setFilters
      }}
    >
      {children}
    </ReportBudgetContext.Provider>
  )
}

export default ReportBudgetProvider
export const ReportBudgetContext = createContext<IBudgetReportContext>(
  {} as IBudgetReportContext
)
